import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Tooltip, Row, Col, App, List } from "antd";
import React, { useState } from "react";
import { defineMessages, useIntl, FormattedMessage } from "react-intl";
import { useCopyToClipboard } from "react-use";

import { formatAssetPath } from "../../../lib/formats/assets";
import {
  formatCommentContent,
  formatCommentFile,
} from "../../../lib/formats/comments";
import { formatDateFromNow, formatDateTime } from "../../../lib/formats/dates";
import { formatUsername } from "../../../lib/formats/users";
import { CommentableType, SortDirection } from "../../../lib/graphql";
import {
  useCommentDiscard,
  useComments,
} from "../../../lib/hooks/basic/comments";
import { useCurrentLocale } from "../../../lib/hooks/basic/locales";
import { useCurrentUser } from "../../../lib/hooks/basic/users";
import { Avatar } from "../Avatar";
import { Dropdown } from "../Dropdown";
import CommentForm from "./CommentForm";

import styles from "./Comments.module.css";

interface CommentListProps {
  commentableType: CommentableType;
  commentableId: string;
}

const messages = defineMessages({
  comment: { id: "comment", defaultMessage: "comment" },
  discardSuccess: { id: "discard.success", defaultMessage: "success" },
  discardConfirm: { id: "discard.confirm", defaultMessage: "confirm" },
  reply: { id: "reply", defaultMessage: "reply" },
  copied: { id: "copyLink.success", defaultMessage: "copied" },
});

function ActionItem({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <li>
      <button type="button" onClick={onClick}>
        {children}
      </button>
    </li>
  );
}

export function Comments({ commentableType, commentableId }: CommentListProps) {
  const { showSuccess } = useCurrentLocale();
  const { modal } = App.useApp();
  const intl = useIntl();
  const entityName = intl.formatMessage(messages.comment);
  const [currentComment, setCurrentComment] = useState<string>();
  const { user } = useCurrentUser();
  const [, copyToClipboard] = useCopyToClipboard();
  const { items } = useComments({
    variables: {
      filter: {
        commentableType: [commentableType],
        commentableId,
        discardedAt: null,
      },
      sort: { createdAt: SortDirection.Asc },
    },
  });
  const [discard] = useCommentDiscard({ refetchQueries: ["Comments"] });
  const currentUserId = user ? user.id : "";
  const onDiscard = (commentId: string) => {
    modal.confirm({
      title: intl.formatMessage(messages.discardConfirm, { entityName }),
      onOk() {
        return discard({ variables: { id: commentId } })
          .then((result) => {
            if (!result.data) return null;

            return result.data.commentDiscard;
          })
          .then((result) => {
            if (result && result.result) {
              showSuccess(
                intl.formatMessage(messages.discardSuccess, { entityName })
              );
            }
          });
      },
      onCancel() {},
    });
  };

  return (
    <div className="comment-list">
      {items &&
        items.map((comment) =>
          comment.id === currentComment ? (
            <CommentForm
              key={comment.id}
              commentableType={commentableType}
              commentableId={commentableId}
              commentId={comment.id}
              content={comment.content}
              defaultFiles={comment.files}
              onChanged={() => setCurrentComment(undefined)}
            />
          ) : (
            <div
              id={`comment-${comment.id}`}
              key={comment.id}
              className={styles.comment}
            >
              <div className={styles.avatar}>
                <Avatar user={comment.user} />
              </div>
              <div className={styles.body}>
                <div className={styles.header}>
                  <Tooltip title={comment.user.email}>
                    <span className={styles.author}>
                      {formatUsername(comment.user)}
                    </span>
                  </Tooltip>
                  <span className={styles.time}>
                    <Row justify="space-between">
                      <Col>
                        <Tooltip title={formatDateTime(comment.createdAt)}>
                          <span>{formatDateFromNow(comment.createdAt)}</span>
                        </Tooltip>
                      </Col>
                      {comment.user.id === currentUserId && (
                        <Col>
                          <Dropdown
                            placement="bottomRight"
                            menu={{
                              triggerSubMenuAction: "click",
                              items: [
                                {
                                  key: "edit",
                                  icon: <EditOutlined />,
                                  onClick: () => setCurrentComment(comment.id),
                                  label: (
                                    <FormattedMessage
                                      id="edit"
                                      defaultMessage="edit"
                                    />
                                  ),
                                },
                                { type: "divider" },
                                {
                                  key: "delete",
                                  danger: true,
                                  icon: <DeleteOutlined />,
                                  onClick: () => onDiscard(comment.id),
                                  label: (
                                    <FormattedMessage
                                      id="delete"
                                      defaultMessage="delete"
                                    />
                                  ),
                                },
                              ],
                            }}
                            trigger={["click"]}
                          >
                            <button
                              type="button"
                              className="text-link"
                              style={{
                                padding: 0,
                                border: 0,
                                fontSize: "16px",
                                background: "none",
                              }}
                              aria-label={intl.formatMessage({
                                id: "actions",
                                defaultMessage: "actions",
                              })}
                            >
                              <MoreOutlined />
                            </button>
                          </Dropdown>
                        </Col>
                      )}
                    </Row>
                  </span>
                </div>
                <div className={styles.content}>
                  <p>
                    {formatCommentContent({
                      comment,
                      canReadUsers: user?.permissions?.user?.read,
                    })}
                  </p>
                  {!!comment.files?.length && (
                    <List
                      size="small"
                      dataSource={comment.files}
                      renderItem={(file) => {
                        const url = formatAssetPath(file.url);

                        return (
                          <List.Item>
                            <List.Item.Meta
                              avatar={formatCommentFile(
                                file.url,
                                file.contentType
                              )}
                              title={
                                <a href={url} target="_blank" rel="noreferrer">
                                  {file.filename}
                                </a>
                              }
                            />
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </div>
                <ul className={styles.actions}>
                  <ActionItem
                    onClick={() => {
                      const textArea = document.querySelector(
                        "textarea"
                      ) as HTMLTextAreaElement;
                      const nativeInputValueSetter = (
                        Object.getOwnPropertyDescriptor(
                          window.HTMLTextAreaElement.prototype,
                          "value"
                        ) as any
                      ).set;
                      nativeInputValueSetter.call(
                        textArea,
                        `@${comment.user.email} `
                      );

                      const inputEvent = new Event("input", { bubbles: true });
                      textArea.dispatchEvent(inputEvent);
                      textArea.focus();
                    }}
                  >
                    {intl.formatMessage(messages.reply)}
                  </ActionItem>
                  <ActionItem
                    onClick={() => {
                      copyToClipboard(`${location.href}#comment-${comment.id}`);
                      showSuccess(intl.formatMessage(messages.copied));
                    }}
                  >
                    <LinkOutlined style={{ marginRight: "2px" }} />
                    <FormattedMessage id="copyLink" defaultMessage="copyLink" />
                  </ActionItem>
                </ul>
              </div>
            </div>
          )
        )}
    </div>
  );
}
export default Comments;
